import { PaginationState } from '@tanstack/react-table';
import { useEffect, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { ColumnData } from '../interface/interface';
import { Table } from '../components/Table';
import instance from '../config/axios';
import Loading from '../components/Loading';
import CustomModal from '../components/CustomModal';
import showToast from '../components/ToastComponent';
import moment from 'moment';

const DeactivateRequestPatientList = () => {
  const { user } = useAuth();
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 50,
  });
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const [search, setSearch] = useState<string>('');
  const [submitSearch, setSubmission] = useState<number>(0);
  const [data, setData] = useState([]);
  const [renderCounter, setRenderCounter] = useState(0);
  const [loading, setLoading] = useState(false);
  const [id, setId] = useState<undefined | string>(undefined);
  const [openModal, setModal] = useState<boolean>(false);

  const requestList = () => {
    instance({
      method: 'GET',
      headers: {
        Authorization: `Bearer ${user?.access_token}`,
      },
      url: `api/v1/deactivate-patient-list?page=${pagination.pageIndex}&limit=${pagination.pageSize}&search=${search}`,
    })
      .then((resp) => {
        setId(undefined);
        setData(resp.data.data);
        setTotalRecords(resp.data.total);
        setLoading(false);
      })
      .catch((err) => {
        console.log(err);
        setLoading(false);
        setId(undefined);
      });
  };

  const deactivatePatient = (id: string) =>
    instance({
      method: 'POST',
      data: { id },
      headers: {
        Authorization: `Bearer ${user?.access_token}`,
      },
      url: '/api/v1/patient/deactivate',
    });

  const confirmDelete = () => {
    if (!id) {
      return;
    }
    deactivatePatient(id)
      .then((res) => {
        setRenderCounter((prev) => prev + 1);
        setModal(false);
        if (res.status === 200) {
          showToast({
            message: res.data.message,
            type: 'success',
          });
        } else {
          showToast({
            message: res.data.message,
            type: 'error',
          });
        }
      })
      .catch((err) => {
        console.log(err);
        setModal(false);
        showToast({
          message: 'Failed to deactivate patient account',
          type: 'error',
        });
      });
  };

  useEffect(() => {
    if (!openModal) {
      setModal(false);
      setId(undefined);
    }
  }, [openModal]);

  const columns: Array<ColumnData> = [
    {
      key: 'patient_name',
      header: () => 'Patient Name',
      cell: (info) => info.getValue(),
      isSortable: true,
    },
    {
      key: 'date_of_birth',
      header: () => 'Date Of Birth',
      cell: (info) => {
        const date = moment(info.getValue()).utc();
        return date.isValid() ? date.format('DD/MM/YYYY') : '-';
      },
      isSortable: true,
    },
    {
      key: 'reason',
      header: () => 'Reason',
      cell: (info) => info.getValue(),
      isSortable: true,
    },

    {
      key: 'req_created_date',
      header: () => 'Submitted At',
      cell: (info) => {
        const date = moment(info.getValue());
        return date.isValid() ? date.format('DD/MM/YYYY HH:mm') : '-';
      },
      isSortable: true,
    },
    {
      key: 'action',
      cell: (info) => (
        <div className="flex justify-around">
          <span
            className="text-green-500 cursor-pointer"
            onClick={() => {
              setId(info.row.original.id);
              setModal(true);
            }}
          >
            Approve
          </span>
        </div>
      ),
    },
  ];

  useEffect(() => {
    setLoading(true);
    requestList();
  }, [pagination, submitSearch, renderCounter]);

  if (loading) {
    return <Loading />;
  }

  if (!data.length) {
    return <h4 className="text-center mt-5">No Request Raised Yet</h4>;
  }

  return (
    <>
      <div className="mx-5 my-5 p-5">
        <div className="px-4 sm:px-6 lg:px-8">
          <div className="sm:flex sm:items-center">
            <div className="sm:flex-auto">
              <h1 className="text-xl font-semibold leading-6 text-primary-blue-500">
                Request For Deactivate Patient
              </h1>
            </div>
          </div>
          <div className="mt-8 flow-root">
            <div className="-mx-4 -my-2 overflow-x-hidden overflow-y-hidden sm:-mx-6 lg:-mx-8 min-h-[600px]">
              <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
                <div className="overflow-hidden sm:rounded-lg">
                  <Table
                    columns={columns}
                    data={data}
                    enableSorting={true}
                    enablePagination={true}
                    enableSearch={true}
                    search={{
                      value: search,
                      changeHandler: setSearch,
                      submitSearch: setSubmission,
                    }}
                    pagintation={{
                      state: pagination,
                      setter: setPagination,
                      totalRecords,
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <CustomModal
        isOpen={openModal}
        onClose={() => setModal(false)}
        header={'Are you sure you want to deactivate the patient profile ?'}
        footer={
          <>
            <button
              className="mr-4 bg-primary-blue-500 text-white px-4 py-2 rounded"
              onClick={() => confirmDelete()}
            >
              Submit
            </button>
            <button
              className="bg-white border border-solid border-primary-blue-500 text-primary-blue-500 px-4 py-2 rounded"
              onClick={() => setModal(false)}
            >
              Cancel
            </button>
          </>
        }
        modalSize={'w-3/12'}
      >
        {''}
      </CustomModal>
    </>
  );
};

export default DeactivateRequestPatientList;
